<template>
  <div class="animated fadeIn kb-detail-page">

    <!-- Upgrade gate -->
    <div v-if="!canCreateKBs" class="pk-upgrade-gate">
      <div class="pk-upgrade-gate__icon"><i class="fas fa-lock"></i></div>
      <h3 class="pk-upgrade-gate__title">{{ $t('kortex.upgradeToUseKBs') }}</h3>
      <p class="pk-upgrade-gate__desc">{{ $t('kortex.upgradeToUseKBsDesc') }}</p>
      <router-link :to="{ name: 'Checkout' }" class="pk-btn-primary">
        {{ $t('kortex.upgradePlan') }}
      </router-link>
    </div>

    <template v-if="canCreateKBs">

    <!-- HEADER -->
    <div class="pk-page-header">
      <div class="pk-page-header__left">
        <router-link
          :to="{ name: 'CortexKnowledgeBases' }"
          class="pk-page-header__back"
        >
          <i class="fas fa-arrow-left"></i>
        </router-link>
        <div class="pk-page-header__icon pk-page-header__icon--teal">
          <i class="fas fa-database"></i>
        </div>
        <div>
          <h2 class="pk-page-header__title">{{ knowledgeBase ? knowledgeBase.name : $t('kortex.knowledgeBaseDetail') }}</h2>
          <p class="pk-page-header__sub" v-if="documents.length > 0">{{ documents.length }} {{ $t('kortex.documents').toLowerCase() }}</p>
        </div>
      </div>
      <button class="pk-btn-primary" @click="showUploadDialog = true">
        <i class="fas fa-upload"></i> {{ $t('kortex.uploadDocument') }}
      </button>
    </div>

    <!-- CARD -->
    <div class="pk-card">
      <pk-data-table
        :columns="columns"
        :rows="documents"
        :loading="loading"
        row-key="uuid"
        empty-icon="fas fa-file-alt"
        :empty-text="$t('kortex.noDocuments')"
      >
        <template #cell(name)="{ row }">
          <strong class="pk-name">{{ row.name }}</strong>
        </template>

        <template #cell(status)="{ row }">
          <span class="pk-status-badge" :class="'pk-status-badge--' + statusVariant(row.processing_status)">
            {{ $t('kortex.status.' + row.processing_status) || row.processing_status }}
          </span>
          <div
            v-if="row.processing_status === 'failed' && row.processing_error"
            class="pk-status-error-reason"
          >
            {{ row.processing_error }}
          </div>
        </template>

        <template #cell(chunk_count)="{ row }">
          <span class="pk-count-badge pk-count-badge--gray">{{ row.chunk_count || 0 }}</span>
        </template>

        <template #cell(created_at)="{ row }">
          <span class="pk-date">{{ formatDate(row.created_at) }}</span>
        </template>

        <template #cell(actions)="{ row }">
          <button
            v-if="row.processing_status === 'failed'"
            class="action-btn action-btn--warning"
            :disabled="retryingUUID === row.uuid"
            :title="$t('kortex.retryProcessing')"
            @click="retryDoc(row)"
          >
            <b-spinner v-if="retryingUUID === row.uuid" small></b-spinner>
            <i v-else class="fas fa-redo"></i>
          </button>
          <button class="action-btn" @click="downloadDoc(row)" :title="$t('download')">
            <i class="fas fa-download"></i>
          </button>
          <button class="action-btn action-btn--danger" @click="confirmDeleteDoc(row)">
            <i class="fas fa-trash"></i>
          </button>
        </template>
      </pk-data-table>
    </div>

    <!-- Upload dialog -->
    <main-dialog
      :show="showUploadDialog"
      :title="$t('kortex.uploadDocument')"
      @close="closeUploadDialog"
    >
      <b-form @submit.prevent="upload">
        <b-form-group :label="$t('kortex.documentName')" label-for="doc-name">
          <b-form-input
            id="doc-name"
            v-model="uploadForm.name"
            :placeholder="$t('kortex.documentNamePlaceholder')"
          ></b-form-input>
          <small class="text-muted">{{ $t('kortex.documentNameHint') }}</small>
        </b-form-group>
        <b-form-group :label="$t('kortex.selectFile')" label-for="doc-file">
          <b-form-file
            id="doc-file"
            v-model="uploadForm.file"
            accept=".pdf,.txt,.md,.docx,.doc,.csv"
            :placeholder="$t('kortex.selectFilePlaceholder')"
          ></b-form-file>
        </b-form-group>
        <b-form-group :label="$t('kortex.chunkingStrategy')" :description="$t('kortex.chunkingStrategyHelp')">
          <div class="kb-preset-pills">
            <button
              v-for="p in chunkingPresets"
              :key="p.value"
              type="button"
              class="kb-preset-pill"
              :class="{ 'kb-preset-pill--selected': uploadForm.chunkingPreset === p.value }"
              @click="selectPreset(p.value)"
            >
              <span class="kb-preset-pill__label">{{ p.label }}</span>
              <span class="kb-preset-pill__hint">{{ p.hint }}</span>
            </button>
          </div>
          <div v-if="uploadForm.chunkingPreset === 'custom'" class="kb-custom-chunk">
            <b-form-group :label="$t('kortex.chunkSize')" label-for="doc-chunk-size" class="mb-2">
              <b-form-input
                id="doc-chunk-size"
                v-model.number="uploadForm.chunkSize"
                type="number"
                min="50"
                max="2000"
              ></b-form-input>
            </b-form-group>
            <b-form-group :label="$t('kortex.chunkOverlap')" label-for="doc-chunk-overlap" class="mb-0">
              <b-form-input
                id="doc-chunk-overlap"
                v-model.number="uploadForm.chunkOverlap"
                type="number"
                min="0"
                max="500"
              ></b-form-input>
            </b-form-group>
          </div>
        </b-form-group>
        <b-alert v-if="uploadError" variant="danger" show dismissible @dismissed="uploadError = ''">
          {{ uploadError }}
        </b-alert>
        <div v-if="uploading" class="text-center py-2">
          <b-spinner variant="primary" small class="mr-2"></b-spinner>
          {{ $t('kortex.uploading') }}
        </div>
      </b-form>
      <template #footer>
        <b-button variant="secondary" :disabled="uploading" @click="closeUploadDialog">
          {{ $t('cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="uploading || !uploadForm.file"
          @click="upload"
        >
          <b-spinner v-if="uploading" small class="mr-1"></b-spinner>
          {{ $t('kortex.upload') }}
        </b-button>
      </template>
    </main-dialog>

    <!-- Delete doc dialog -->
    <main-dialog
      :show="showDeleteDocDialog"
      :title="$t('kortex.deleteDocument')"
      @close="showDeleteDocDialog = false"
    >
      <p>{{ $t('kortex.confirmDeleteDoc') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showDeleteDocDialog = false">
          {{ $t('cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="deleting" @click="doDeleteDoc">
          <b-spinner v-if="deleting" small class="mr-1"></b-spinner>
          {{ $t('delete') }}
        </b-button>
      </template>
    </main-dialog>

    </template>
  </div>
</template>

<script>
export default {
  name: 'KnowledgeBaseDetail',
  data: function () {
    return {
      loading: false,
      uploading: false,
      deleting: false,
      retryingUUID: null,
      showUploadDialog: false,
      showDeleteDocDialog: false,
      pendingDeleteDoc: null,
      uploadError: '',
      knowledgeBase: null,
      documents: [],
      uploadForm: {
        name: '',
        file: null,
        chunkingPreset: 'balanced',
        chunkSize: 300,
        chunkOverlap: 40
      },
      fields: [],
      columns: [
        { key: 'name', label: this.$t('kortex.documentName') },
        { key: 'status', label: this.$t('kortex.status.label') },
        { key: 'chunk_count', label: this.$t('kortex.chunks') },
        { key: 'created_at', label: this.$t('createdAt') },
        { key: 'actions', label: '' }
      ]
    }
  },
  computed: {
    canCreateKBs: function () {
      return this.$store.getters['cortex/canCreateKBs']
    },
    chunkingPresets: function () {
      return [
        { value: 'balanced', label: this.$t('kortex.chunkPresetBalanced'), hint: this.$t('kortex.chunkPresetBalancedHint'), size: 300, overlap: 40 },
        { value: 'longform', label: this.$t('kortex.chunkPresetLongform'), hint: this.$t('kortex.chunkPresetLongformHint'), size: 700, overlap: 80 },
        { value: 'shortqa', label: this.$t('kortex.chunkPresetShortqa'), hint: this.$t('kortex.chunkPresetShortqaHint'), size: 150, overlap: 20 },
        { value: 'custom', label: this.$t('kortex.chunkPresetCustom'), hint: this.$t('kortex.chunkPresetCustomHint'), size: null, overlap: null }
      ]
    }
  },
  mounted: function () {
    this.$store.dispatch('cortex/loadTier')
    this.loadKb()
    this.loadDocuments()

    // ── Kortex AI tools ──────────────────────────────────────
    if (!window.kortex) return

    window.kortex.showHelper()
    window.kortex.registerContext(
      'Knowledge Base Detail',
      'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
      'The user is viewing a Kortex knowledge base. You can see the KB metadata, list all documents with their processing ' +
      'status (pending/processing/completed/failed), check specific document status, and refresh the document list. ' +
      'Use get_kb_info for an overview, list_kb_documents to see all files, and get_document_status to check a specific file.',
      'fa-database'
    )

    var self = this

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_kb_info',
          description: 'Returns metadata about the current knowledge base: name, UUID, and document count.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        var kb = self.knowledgeBase
        if (!kb) return { error: 'Knowledge base not loaded yet' }
        return {
          uuid: kb.uuid,
          name: kb.name,
          description: kb.description || '',
          document_count: self.documents.length,
          completed_documents: self.documents.filter(function (d) { return d.processing_status === 'completed' || d.processing_status === 'processed' }).length,
          processing_documents: self.documents.filter(function (d) { return d.processing_status === 'processing' }).length,
          failed_documents: self.documents.filter(function (d) { return d.processing_status === 'failed' }).length
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_kb_documents',
          description: 'Lists all documents in the knowledge base with their names, status, chunk count, and creation date.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        var docs = self.documents.map(function (d) {
          return {
            uuid: d.uuid,
            name: d.name,
            status: d.processing_status,
            error: d.processing_error || null,
            chunks: d.chunk_count || 0,
            created_at: d.created_at
          }
        })
        return { documents: docs, count: docs.length }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_document_status',
          description: 'Returns the processing status of a specific document by name (partial match supported).',
          parameters: {
            type: 'object',
            required: ['name'],
            properties: {
              name: { type: 'string', description: 'The document name or partial name to search for.' }
            }
          }
        }
      },
      function (args) {
        var name = (args.name || '').toLowerCase()
        var matches = self.documents.filter(function (d) { return d.name.toLowerCase().indexOf(name) !== -1 })
        if (matches.length === 0) return { error: 'No document found matching: ' + args.name }
        return {
          matches: matches.map(function (d) {
            return {
              uuid: d.uuid,
              name: d.name,
              status: d.processing_status,
              error: d.processing_error || null,
              chunks: d.chunk_count || 0,
              created_at: d.created_at
            }
          })
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'refresh_kb_documents',
          description: 'Reloads the document list from the server to get the latest processing statuses.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        self.loadDocuments()
        self.loadKb()
        return { refreshed: true, note: 'Document list refresh triggered.' }
      }
    )
  },

  beforeDestroy: function () {
    if (!window.kortex) return
    window.kortex.unregisterContext()
    window.kortex.hideHelper()
    window.kortex.unregisterTool('get_kb_info')
    window.kortex.unregisterTool('list_kb_documents')
    window.kortex.unregisterTool('get_document_status')
    window.kortex.unregisterTool('refresh_kb_documents')
  },
  methods: {
    loadKb: function () {
      var self = this
      self.$http.get('/luma/kortex/v1/knowledge-bases/' + self.$route.params.uuid).then(function (resp) {
        self.knowledgeBase = resp.data
      })
    },
    loadDocuments: function () {
      var self = this
      self.loading = true
      self.$http.get('/luma/kortex/v1/knowledge-bases/' + self.$route.params.uuid + '/documents', {
        params: { limit: 100, page: 1 }
      }).then(function (resp) {
        self.documents = resp.data.data || []
      }).finally(function () {
        self.loading = false
      })
    },
    selectPreset: function (value) {
      this.uploadForm.chunkingPreset = value
      for (var i = 0; i < this.chunkingPresets.length; i++) {
        var p = this.chunkingPresets[i]
        if (p.value === value && p.size !== null) {
          this.uploadForm.chunkSize = p.size
          this.uploadForm.chunkOverlap = p.overlap
        }
      }
    },
    upload: function () {
      var self = this
      if (!self.uploadForm.file) return

      self.uploading = true
      self.uploadError = ''

      var size = Number(self.uploadForm.chunkSize) || 300
      var overlap = Number(self.uploadForm.chunkOverlap)
      if (isNaN(overlap) || overlap < 0) overlap = 0
      // Overlap must stay below the chunk size; mirror the backend's safe clamp.
      if (overlap >= size) overlap = Math.floor(size / 4)

      var kbUUID = self.$route.params.uuid
      var formData = new FormData()
      formData.append('file', self.uploadForm.file)
      if (self.uploadForm.name) {
        formData.append('name', self.uploadForm.name)
      }
      formData.append('chunk_size', size)
      formData.append('chunk_overlap', overlap)

      self.$http.post('/luma/kortex/v1/knowledge-bases/' + kbUUID + '/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function () {
        self.closeUploadDialog()
        self.loadDocuments()
      }).catch(function (err) {
        self.uploadError = err && err.message ? err.message : self.$t('kortex.uploadFailed')
      }).finally(function () {
        self.uploading = false
      })
    },
    downloadDoc: function (doc) {
      var kbUUID = this.$route.params.uuid
      window.open('/luma/kortex/v1/knowledge-bases/' + kbUUID + '/documents/' + doc.uuid + '/download', '_blank')
    },
    confirmDeleteDoc: function (doc) {
      this.pendingDeleteDoc = doc
      this.showDeleteDocDialog = true
    },
    doDeleteDoc: function () {
      var self = this
      if (!self.pendingDeleteDoc) return
      self.deleting = true
      self.$http.delete('/luma/kortex/v1/knowledge-bases/' + self.$route.params.uuid + '/documents/' + self.pendingDeleteDoc.uuid).then(function () {
        self.showDeleteDocDialog = false
        self.pendingDeleteDoc = null
        self.loadDocuments()
      }).finally(function () {
        self.deleting = false
      })
    },
    retryDoc: function (doc) {
      var self = this
      self.retryingUUID = doc.uuid
      self.$http.post('/luma/kortex/v1/knowledge-bases/' + self.$route.params.uuid + '/documents/' + doc.uuid + '/retry').then(function () {
        self.loadDocuments()
      }).catch(function () {
        self.loadDocuments()
      }).finally(function () {
        self.retryingUUID = null
      })
    },
    closeUploadDialog: function () {
      this.showUploadDialog = false
      this.uploadForm = { name: '', file: null, chunkingPreset: 'balanced', chunkSize: 300, chunkOverlap: 40 }
      this.uploadError = ''
    },
    statusVariant: function (status) {
      var map = {
        completed: 'success',
        processed: 'success',
        processing: 'warning',
        pending: 'secondary',
        failed: 'danger',
        error: 'danger'
      }
      return map[status] || 'secondary'
    },
    formatDate: function (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.pk-page-header__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: var(--pk-text-muted, #94a3b8);
  margin-right: 8px;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
}
.pk-page-header__back:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--pk-text, #e2e8f0);
}

.pk-status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.pk-status-badge--success {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}
.pk-status-badge--warning {
  background: rgba(234, 179, 8, 0.15);
  color: #facc15;
}
.pk-status-badge--danger {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}
.pk-status-badge--secondary {
  background: rgba(148, 163, 184, 0.12);
  color: #94a3b8;
}
.pk-status-error-hint {
  display: inline-block;
  margin-left: 5px;
  color: #f87171;
  cursor: help;
  font-size: 12px;
  vertical-align: middle;
}
.pk-status-error-reason {
  margin-top: 4px;
  font-size: 11px;
  color: #f87171;
  line-height: 1.4;
  max-width: 220px;
  word-break: break-word;
}

.action-btn--warning {
  color: #facc15;
}
.action-btn--warning:hover {
  background: rgba(234, 179, 8, 0.15);
}

.pk-upgrade-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  gap: 16px;
}
.pk-upgrade-gate__icon {
  font-size: 48px;
  color: var(--pk-text-muted, #94a3b8);
}
.pk-upgrade-gate__title {
  font-size: 20px;
  font-weight: 600;
  color: var(--pk-text, #e2e8f0);
  margin: 0;
}
.pk-upgrade-gate__desc {
  color: var(--pk-text-muted, #94a3b8);
  max-width: 400px;
  margin: 0;
}

/* Chunking preset selector */
.kb-preset-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.kb-preset-pill {
  flex: 1 1 calc(50% - 8px);
  min-width: 130px;
  text-align: left;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.kb-preset-pill:hover {
  border-color: rgba(108,93,211,0.6);
}
.kb-preset-pill--selected {
  border-color: #6c5dd3;
  background: rgba(108,93,211,0.12);
}
.kb-preset-pill__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
}
.kb-preset-pill__hint {
  display: block;
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  margin-top: 2px;
  line-height: 1.4;
}
.kb-custom-chunk {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
}
</style>
